/*
 *
 * Copyright (c) 1999-2022 Luciad All Rights Reserved.
 *
 * Luciad grants you ("Licensee") a non-exclusive, royalty free, license to use,
 * modify and redistribute this software in source and binary code form,
 * provided that i) this copyright notice and license appear on all copies of
 * the software; and ii) Licensee does not utilize the software in a manner
 * which is disparaging to Luciad.
 *
 * This software is provided "AS IS," without a warranty of any kind. ALL
 * EXPRESS OR IMPLIED CONDITIONS, REPRESENTATIONS AND WARRANTIES, INCLUDING ANY
 * IMPLIED WARRANTY OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE OR
 * NON-INFRINGEMENT, ARE HEREBY EXCLUDED. LUCIAD AND ITS LICENSORS SHALL NOT BE
 * LIABLE FOR ANY DAMAGES SUFFERED BY LICENSEE AS A RESULT OF USING, MODIFYING
 * OR DISTRIBUTING THE SOFTWARE OR ITS DERIVATIVES. IN NO EVENT WILL LUCIAD OR ITS
 * LICENSORS BE LIABLE FOR ANY LOST REVENUE, PROFIT OR DATA, OR FOR DIRECT,
 * INDIRECT, SPECIAL, CONSEQUENTIAL, INCIDENTAL OR PUNITIVE DAMAGES, HOWEVER
 * CAUSED AND REGARDLESS OF THE THEORY OF LIABILITY, ARISING OUT OF THE USE OF
 * OR INABILITY TO USE SOFTWARE, EVEN IF LUCIAD HAS BEEN ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGES.
 */
import {SimpleBalloonContentProvider} from "@luciad/ria-sample-common/balloon/SimpleBalloonContentProvider";
import {getFitBounds} from "@luciad/ria-sample-common/util/FitBoundsUtil";
import {FeatureLayer} from "@luciad/ria/view/feature/FeatureLayer";
import {Layer} from "@luciad/ria/view/Layer";
import {LayerTreeNode} from "@luciad/ria/view/LayerTreeNode";
import {LayerTreeNodeType} from "@luciad/ria/view/LayerTreeNodeType";
import {LayerTreeVisitor} from "@luciad/ria/view/LayerTreeVisitor";
import {LayerType} from "@luciad/ria/view/LayerType";
import {Map} from "@luciad/ria/view/Map";
import {GeoJsonDataLoader} from "./loaders/GeoJsonDataLoader";
import {GMLDataLoader} from "./loaders/GMLDataLoader";
import {KMLDataLoader} from "./loaders/KMLDataLoader";

export const addLayer = (map: Map, aLayer: LayerTreeNode | Promise<LayerTreeNode>,
                         skipFit: boolean = false): Promise<LayerTreeNode> => {
  return Promise.resolve(aLayer).then((layer) => {
    if (layer instanceof FeatureLayer) {
      if (!layer.balloonContentProvider) {
        layer.balloonContentProvider = SimpleBalloonContentProvider;
      }
    }

    if (!skipFit) {
      getFitBounds(layer).then(fitBounds => {
        if (fitBounds) {
          map.mapNavigator.fit({
            bounds: fitBounds,
            animate: true
          })
        }
      });
    }

    if (layer.treeNodeType === LayerTreeNodeType.LAYER && (layer as Layer).type === LayerType.BASE) {
      const referenceLayer = findBaseLayer(map);
      if (referenceLayer !== null) {
        map.layerTree.addChild(layer, "above", referenceLayer);
      } else {
        map.layerTree.addChild(layer, "bottom");
      }
    } else {
      map.layerTree.addChild(layer, "top");
    }

    layer.visible = true;
    return layer;
  });
}

export async function openFileOrUrl(map: Map, urlOrFile: File | string): Promise<void> {
  const fileName = (typeof urlOrFile === 'string') ? urlOrFile : urlOrFile.name;
  const extension = fileName.toLowerCase().replace(/.*\./, "");
  const layerName = fileName.replace(/.*\//, "").replace(/\.[^.]*$/, "");
  if (extension === "json" || extension === "geojson") {
    await addLayer(map, GeoJsonDataLoader.createLayer(layerName, urlOrFile));
  } else if (extension.indexOf("gml") > -1 || extension === "xml") {
    await addLayer(map, GMLDataLoader.createLayer(layerName, urlOrFile, undefined, ["EPSG:4326"]));
  } else if (extension === "kml" || extension === "kmz") {
    if (fileName !== urlOrFile) {
      urlOrFile = URL.createObjectURL(urlOrFile);
    }
    await addLayer(map, KMLDataLoader.createLayer(layerName, urlOrFile));
  } else {
    throw new Error("Unknown file format for " + fileName);
  }
}

function findBaseLayer(map: Map) {
  let baseLayer: Layer | null = null;
  const visitor: LayerTreeVisitor = {
    visitLayer: (layer) => {
      if (layer.type === LayerType.BASE) {
        baseLayer = layer;
        return LayerTreeVisitor.ReturnValue.ABORT;
      }
      return LayerTreeVisitor.ReturnValue.CONTINUE;
    },
    visitLayerGroup: (layerGroup) => {
      layerGroup.visitChildren(visitor, LayerTreeNode.VisitOrder.TOP_DOWN);
      return (baseLayer !== null) ? LayerTreeVisitor.ReturnValue.ABORT : LayerTreeVisitor.ReturnValue.CONTINUE;
    }
  }
  map.layerTree.accept(visitor);
  return baseLayer;
}